import { sendRequest } from "@infrastructure/api/api";
import type ApiResponse from "@core/types/apiResponse";
import type { ApiMessageResponse } from "@core/types/apiMessageResponse";
import type { ErrorResponse } from "@core/types/errorResponse";
import type { DepartmentReportScope } from "./requestDepartmentReportSave";

export type DepartmentReportMediaArgs =
  | {
      scope: "church";
      churchId: number;
      departmentId: number;
      departmentReportId: number;
      mediaStorageId: number;
    }
  | {
      scope: "district";
      districtId: number;
      departmentId: number;
      departmentReportId: number;
      mediaStorageId: number;
    }
  | {
      scope: "nation";
      nationId: number;
      departmentId: number;
      departmentReportId: number;
      mediaStorageId: number;
    };

function buildBasePath(scope: DepartmentReportScope, args: DepartmentReportMediaArgs): string {
  if (scope === "church") return `/church/${(args as Extract<DepartmentReportMediaArgs, { scope: "church" }>).churchId}`;
  if (scope === "district") return `/district/${(args as Extract<DepartmentReportMediaArgs, { scope: "district" }>).districtId}`;
  return `/nation/${(args as Extract<DepartmentReportMediaArgs, { scope: "nation" }>).nationId}`;
}

export function buildDepartmentReportMediaPath(args: DepartmentReportMediaArgs): string {
  return `${buildBasePath(args.scope, args)}/department/${args.departmentId}/report/${args.departmentReportId}/media/${args.mediaStorageId}`;
}

export async function removeDepartmentReportMediaAsync(
  args: DepartmentReportMediaArgs
): Promise<ApiResponse<ApiMessageResponse, ErrorResponse>> {
  return sendRequest<undefined, ApiMessageResponse>(
    "DELETE",
    buildDepartmentReportMediaPath(args)
  );
}

export async function fetchDepartmentReportMediaAsync(
  args: DepartmentReportMediaArgs
): Promise<ApiResponse<Blob, ErrorResponse>> {
  return sendRequest<undefined, Blob>(
    "GET",
    buildDepartmentReportMediaPath(args),
    undefined,
    { responseType: "blob" }
  );
}

/**
 * Builds the scope-specific media args from a generic entity id, so callers
 * (the own-media hook and the child-media popover) don't each switch on scope.
 */
export function buildDepartmentReportMediaArgs(
  scope: DepartmentReportScope,
  entityId: number,
  departmentId: number,
  departmentReportId: number,
  mediaStorageId: number
): DepartmentReportMediaArgs {
  const base = { departmentId, departmentReportId, mediaStorageId };
  if (scope === "church") return { scope, churchId: entityId, ...base };
  if (scope === "district") return { scope, districtId: entityId, ...base };
  return { scope, nationId: entityId, ...base };
}

/**
 * Fetches a media attachment (authenticated, as a blob) and opens it in a new tab.
 * The browser renders PDFs/images inline and falls back to downloading Office files.
 *
 * Popup-blocker-safe: the blank tab is opened synchronously within the click gesture,
 * then navigated once the blob resolves. The object URL is revoked after a delay so the
 * tab has time to load. Returns an error message on failure (caller surfaces it), else null.
 */
export async function viewDepartmentReportMediaInNewTab(
  args: DepartmentReportMediaArgs
): Promise<string | null> {
  // Must run synchronously in the click gesture or the browser blocks the popup.
  const win = window.open("", "_blank");
  try {
    const result = await fetchDepartmentReportMediaAsync(args);
    if (!result.isSuccess()) {
      throw new Error(result.getError()?.detail || "Failed to open attachment.");
    }
    const blob = result.getData();
    if (!blob) throw new Error("Failed to open attachment.");

    const objectUrl = URL.createObjectURL(blob);
    if (win) {
      win.location.href = objectUrl;
    } else {
      // Popup was blocked despite the synchronous open — fall back to current tab.
      window.location.href = objectUrl;
    }
    // Give the tab time to load before releasing the blob URL.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return null;
  } catch (e) {
    win?.close();
    return e instanceof Error ? e.message : "Failed to open attachment.";
  }
}
